import { useState, useCallback, useRef, useEffect } from "react";
import Loader from "react-loader-spinner";
import { useDebouncedPage } from "./hooks/useDebouncedPage";
import Restricted from "PermissionProvider/Restricted";
import { useEvaluationQuery } from "./hooks/useEvaluationQuery";
import { useEvaluationFilter } from "./hooks/useEvaluationFilter";
import { Pagination } from "react-bootstrap";
import SelectWithLabel from "components/UI/SelectWithLabel";
import EvaluationListTranslation from "./EvaluationListTranslation";
import EvaluationModal from "./EvaluationModal";
import EvaluationEditModal from "./EvaluationEditModal";
import { DropdownSelect } from "components/UI/ComplexComponents/DropdownSelect";
import { Modal, Button } from 'react-bootstrap'
import { ReactDataContext } from "@themost/react";

export default function EvaluationList(props) {

    const initialState = {
        idSelected: null,
        dateSelected: null,
        typeSelected: null,
        departmentSelected: null,
        universitySelected: null,
        erasmusCodeSelected: null,
        countrySelected: null,
        departmentalCoordinatorSelected: null,
        sortSelected: null,
        isModalOpen: false
    }

    const getCookie = (name) => {
        const cookie = {}
        document.cookie.split(';').forEach((e) => {
            const [key, value] = e.split('=');
            cookie[key.trim()] = value;
        })
        return cookie[name];
    }

    const [langCode, setLangCode] = useState(getCookie("lang") ? getCookie("lang") : "en")
    const [selectedRow, setSelectedRow] = useState(null)

    const filters = useEvaluationFilter(initialState)
    const {
        state,
        setDateSelected,
        setDepartmentSelected,
        setUniversitySelected,
        setErasmusCodeSelected,
        setCountrySelected,
        setDepartmentalCoordinatorSelected,
        setIiaDepartmentSelected,
        setSortSelected,
        setIsModalOpen
    } = filters;

    const {
        dateSelected,
        departmentSelected,
        universitySelected,
        erasmusCodeSelected,
        countrySelected,
        departmentalCoordinatorSelected,
        iiaDepartmentSelected,
        sortSelected,
        isModalOpen
    } = state;

    const { evaluation, loading, page, departments, countries, applicationYears, iiaDepartments, departmentalCoordinators, facultyDepartmentId, facultyDepartmentName, setPage } = useEvaluationQuery(filters, props.userService, langCode)

    const [showPlacementModal, setShowPlacementModal] = useState(false);
    const [placementProggress, setPlacementProggress] = useState({ header: "", body: "", inProgress: false });
    const [placementData, setPlacementData] = useState({ studentEvaluations: null, iiaList: null });
    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const setPageDebounced = useCallback(useDebouncedPage((nextPage) => setPage(nextPage), 500), [setPage])
    const topRef = useRef(null);

    // Auto-set department filter for faculty users
    useEffect(() => {
        if (facultyDepartmentName && !departmentSelected) {
            console.log('DEBUG facultyDepartmentName:', facultyDepartmentName, typeof facultyDepartmentName);
            setDepartmentSelected(facultyDepartmentName);
        }
    }, [facultyDepartmentName, departmentSelected, setDepartmentSelected]);

    if (loading) {
        return <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)'
        }}>
            <Loader
                className="spinner mx-auto"
                type="TailSpin"
                color="#3A435E"
                height={70}
                width={70}
            />
        </div>
    } else {
        let paginationItems = [];
        const pageLimit = 10;
        const hasMandatoryFilters = Boolean(departmentSelected && dateSelected);
        const sortingOptions = EvaluationListTranslation("sorting_opts", langCode)['label'];
        const selectedSortingLabel = sortingOptions.find(i => i.value === sortSelected)?.label || "";
        const selectedDepartmentalCoordinatorLabel = (departmentalCoordinators || []).find(
            (coordinator) => String(coordinator?.id) === String(departmentalCoordinatorSelected)
        )?.label || "";
        const selectedCountryLabel = (countries || []).find(
            (country) => String(country?.id) === String(countrySelected)
        )?.countryName || "";
        const selectedIiaDepartmentLabel = (iiaDepartments || []).find(
            (dept) => String(dept?.id) === String(iiaDepartmentSelected)
        )?.[langCode === "gr" ? "nameGR" : "name"] || "";
        const selectedDepartmentalCoordinatorId = departmentalCoordinatorSelected ? String(departmentalCoordinatorSelected) : null;
        const rows = (evaluation || []).reduce((acc, application) => {
            (application?.prefferedMobility || []).forEach((mobility, prefIndex) => {
                if (
                    selectedDepartmentalCoordinatorId &&
                    String(mobility?.iiaId?.departmentalCoordinatorContacts?.[1]?.id) !== selectedDepartmentalCoordinatorId
                ) {
                    return;
                }

                if (facultyDepartmentId) {
                    if (iiaDepartmentSelected) {
                        // IIA department filter: show mobilities where selected department is a coordinator
                        // (bypasses faculty department check to allow viewing all co-coordinator mobilities)
                        const hasMatchingIiaDepartment = (mobility?.iiaId?.departmentalCoordinatorContacts || []).some(
                            contact => contact?.department?.id === iiaDepartmentSelected
                        );

                        if (!hasMatchingIiaDepartment) {
                            return;
                        }
                    } else {
                        // Default faculty filter: only show mobilities where faculty's department is a coordinator
                        const hasMatchingDepartment = (mobility?.iiaId?.departmentalCoordinatorContacts || []).some(
                            contact => contact?.department?.id === facultyDepartmentId
                        );
                        if (!hasMatchingDepartment) {
                            return;
                        }
                    }
                }

                acc.push({
                    key: `${application?.id}_${prefIndex}`,
                    application,
                    mobility,
                    prefIndex,
                    finalScore: application?.prefferedMobility?.[prefIndex]?.evaluationData?.[0]?.finalScore,
                    approval: application?.prefferedMobility?.[prefIndex]?.evaluationData?.[0]?.approval,
                    preferenceOrder: mobility?.preferenceOrder
                })
            })
            return acc
        }, []);

        const getSortValue = (value) => value === null || value === undefined ? "" : String(value);
        const sortRows = (values) => {
            if (!sortSelected) {
                return values;
            }

            const sortedValues = [...values];
            const compare = (valueA, valueB, direction) => {
                const comparison = getSortValue(valueA).localeCompare(getSortValue(valueB), undefined, { numeric: true, sensitivity: "base" });
                return direction === "asc" ? comparison : comparison * -1;
            }

            if (sortSelected === "order_asc") {
                sortedValues.sort((a, b) => compare(a?.preferenceOrder, b?.preferenceOrder, "asc"));
            } else if (sortSelected === "order_desc") {
                sortedValues.sort((a, b) => compare(a?.preferenceOrder, b?.preferenceOrder, "desc"));
            } else if (sortSelected === "evaluation_asc") {
                sortedValues.sort((a, b) => compare(a?.finalScore, b?.finalScore, "asc"));
            } else if (sortSelected === "evaluation_desc") {
                sortedValues.sort((a, b) => compare(a?.finalScore, b?.finalScore, "desc"));
            }

            return sortedValues;
        }

        const sortedRows = sortRows(rows);
        const totalRows = sortedRows.length;
        const paginatedRows = sortedRows.slice(page * pageLimit, (page + 1) * pageLimit);

        if (page === 0) {
            for (let i = 0; i < (Math.ceil(totalRows / pageLimit) > 3 ? 3 : Math.ceil(totalRows / pageLimit)); i++) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
            }
        } else if (page === Math.ceil(totalRows / pageLimit) - 1) {
            for (let i = Math.ceil(totalRows / pageLimit) - 1; i >= Math.ceil(totalRows / pageLimit) - 3 && i >= 0; i--) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
                paginationItems.sort((element1, element2) => {
                    if (element1.key < element2.key) return -1;
                    if (element1.key > element2.key) return 1;
                    return 0;
                })
            }
        } else {
            for (let i = page - 1; i < page + 2; i++) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
            }
        }
        function setFieldValue(setterFunc) {
            return function (item, value) {
                setterFunc(value)
            }
        }

        async function openPlacementModal() {

            setPlacementProggress({ header: "Loading Placement Data.", body: "Please wait.", inProgress: false });

            setShowPlacementModal(true);

            const context = new ReactDataContext(process.env.REACT_APP_API_SERVER);
            context.setBearerAuthorization(props.userService.getToken());

            let query = context.model(`ApplicationStudentStudiesForms`).asQueryable()
                .where("id").greaterThan(0)
                .and("isDeleted").equal(false)
                .and("status").equal(["validated", "awaiting_placement", "placement_finished"]);

            if (filters.state.dateSelected) {
                query = query.and('applicationYear').equal(filters.state.dateSelected)
            }

            if (filters.state.departmentSelected) {
                const departmentQueryField = langCode === "gr" ? "studiesData/department/nameGR" : "studiesData/department/name";
                query = query.and(departmentQueryField).equal(filters.state.departmentSelected)
            }

            const studentEvaluationResults = await query.getItems();

            let phdNominations = [];
            let phdEvaluations = [];

            let postgraduateNominations = [];
            let postgraduateEvaluations = [];

            let studentNominations = [];
            let studentEvaluations = [];

            let total = studentEvaluationResults.length;
            let validated = [];
            let evaluationFinished = [];
            let placementFinished = [];

            let iiaList = [];

            studentEvaluationResults.forEach((result) => {
                if (result.status == "validated") {
                    validated.push(result.id);
                }
                else if (result.status == "awaiting_placement") {
                    evaluationFinished.push(result.id);
                }
                else if (result.status == "placement_finished") {
                    placementFinished.push(result.id);
                }

                if (result.studiesData?.length == 1) {
                    if (result.studiesData[0].studyCycle == 8) {
                        phdNominations[result.id] = result;

                        if (result.prefferedMobility?.length > 0) {
                            result.prefferedMobility.forEach((mobility) => {
                                if (mobility.evaluationData?.length == 1) {
                                    let tmp = mobility.evaluationData[0];
                                    tmp.applicationId = result.id;
                                    tmp.iia = mobility.iiaId;
                                    tmp.preferenceOrder = mobility.preferenceOrder;
                                    phdEvaluations.push(tmp);
                                }
                            })
                        }
                    }
                    else if (result.studiesData[0].studyCycle == 7) {
                        postgraduateNominations[result.id] = result;

                        if (result.prefferedMobility?.length > 0) {
                            result.prefferedMobility.forEach((mobility) => {
                                if (mobility.evaluationData?.length == 1) {
                                    let tmp = mobility.evaluationData[0];
                                    tmp.applicationId = result.id;
                                    tmp.iia = mobility.iiaId;
                                    tmp.preferenceOrder = mobility.preferenceOrder;
                                    postgraduateEvaluations.push(tmp);
                                }
                            })
                        }
                    }
                    else if (result.studiesData[0].studyCycle == 6) {
                        studentNominations[result.id] = result;

                        if (result.prefferedMobility?.length > 0) {
                            result.prefferedMobility.forEach((mobility) => {
                                if (mobility.evaluationData?.length == 1) {
                                    let tmp = mobility.evaluationData[0];
                                    tmp.applicationId = result.id;
                                    tmp.iia = mobility.iiaId;
                                    tmp.preferenceOrder = mobility.preferenceOrder;
                                    studentEvaluations.push(tmp);
                                }
                            })
                        }
                    }
                }

                if (result.prefferedMobility?.length > 0) {
                    result.prefferedMobility.forEach((mobility) => {


                        if (!iiaList['' + mobility.iiaId?.id]) {
                            mobility.iiaId?.studentMobilitiesForStudies?.forEach((studMob) => {
                                if (studMob.fromInstitution?.id == 1) {
                                    iiaList['' + mobility.iiaId?.id] = { iiaId: mobility.iiaId, mobilityNumber: studMob.mobilitiesPerYear, filledPositions: 0 }
                                }
                            })
                        }
                    })
                }

                // console.log(result.prefferedMobility)
            });

            let totalReady = evaluationFinished.length + placementFinished.length;


            setPlacementProggress(
                {
                    header: totalReady + " out of total " + total + " applications are ready for placement.",
                    body: "If you start the automatic placement proccess " + validated.length + " entries will be ignored and all existing placements will be overwritten.",
                    inProgress: false
                });
            setPlacementData({
                phdEvaluations: phdEvaluations,
                postgraduateEvaluations: postgraduateEvaluations,
                studentEvaluations: studentEvaluations,
                iiaList: iiaList
            });
        }

        // placement number explanation
        // 1 --> succesfully placed
        // 0 --> disabled from succesfull higher prefference
        // -1 --> disabled from low score. In the function bellow once we get -1 somewhere it is permanent
        async function startAutoPlacement() {
            console.log(placementData)
            let evaluations = placementData.studentEvaluations;
            let iiaList = placementData.iiaList;

            let progress = 0;

            setPlacementProggress(
                {
                    header: "Automatic placement in progress. Please wait.",
                    body: "0 out of " + evaluations.length + " entries placed.",
                    inProgress: true
                });

            evaluations.sort((a, b) => a.iia.id - b.iia.id || a.preferenceOrder - b.preferenceOrder || b.finalScore - a.finalScore);

            // place all 1st preferences that fit in iias. after sorting should only get highest scores
            evaluations.forEach((evaluation) => {
                if (evaluation.preferenceOrder == 1) {
                    if (iiaList[evaluation.iia.id].filledPositions < iiaList[evaluation.iia.id].mobilityNumber) {
                        iiaList[evaluation.iia.id].filledPositions++;

                        // evaluation.approval = iiaList[evaluation.iia.id].filledPositions;
                        evaluation.approval = 1;

                        // we placed the eval so we disable all others from the same application with lower preference.
                        // The ones with higher prefference should already be disabled
                        evaluations.forEach((evaluation1) => {
                            if (evaluation1.applicationId == evaluation.applicationId && evaluation1.id != evaluation.id) {
                                evaluation1.approval = 0;
                            }
                        })
                    }
                    else { evaluation.approval = -1; }
                }

                if (evaluation.approval === null) {
                    progress++;
                }
            })

            let evalChanged2 = true;
            let evalChanged3 = true;

            while (evalChanged2 || evalChanged3) {

                setPlacementProggress(
                    {
                        header: "Automatic placement in progress. Please wait.",
                        body: progress + " out of " + evaluations.length + " entries placed.",
                        inProgress: true
                    });

                if (evalChanged2) {
                    evalChanged2 = false;
                    progress = 0;
                    // place 2nd preferences that fit in iias
                    evaluations.forEach((evaluation) => {
                        if (evaluation.preferenceOrder == 2 && evaluation.approval === null) {
                            if (iiaList[evaluation.iia.id].filledPositions < iiaList[evaluation.iia.id].mobilityNumber) {
                                iiaList[evaluation.iia.id].filledPositions++;

                                // evaluation.approval = iiaList[evaluation.iia.id].filledPositions;
                                evaluation.approval = 2;

                                // we placed the eval so we disable all others from the same application with lower preference.
                                // The ones with higher prefference should already be disabled with negative score
                                evaluations.forEach((evaluation1) => {
                                    if (evaluation1.applicationId == evaluation.applicationId && evaluation1.id != evaluation.id && evaluation1.approval === null) {
                                        evaluation1.approval = 0;
                                    }
                                })
                            }
                            // if it doesn't fit, we find other entries with lower score for the same iia and remove the lowest one
                            else if (iiaList[evaluation.iia.id].filledPositions >= iiaList[evaluation.iia.id].mobilityNumber) {
                                let tmp = [];

                                evaluations.forEach((evaluation1) => {
                                    if (evaluation.iia.id == evaluation1.iia.id && evaluation.finalScore > evaluation1.finalScore) {
                                        tmp.push(evaluation1);
                                    }
                                })
                                if (tmp.length > 0) {
                                    tmp.sort((a, b) => a.finalScore - b.finalScore);

                                    evaluation.approval = iiaList[evaluation.iia.id].filledPositions;
                                    tmp[0].approval = -1;
                                    evaluations.forEach((evaluation1) => {
                                        if (evaluation1.applicationId == tmp[0].applicationId && evaluation1.id != tmp[0].id && evaluation1.approval === 0) {
                                            evaluation1.approval = null;
                                        }
                                    })

                                    // if we replaced an existing evaluation, if it had prefferenceOrder 1, we enabled some new entries with prefferenceOrder 2, 
                                    // so we repeat step 2 until nothing changes, before moving to step 3
                                    if (tmp[0].preferenceOrder == 1) {
                                        evalChanged2 = true;
                                    }
                                }
                            }
                            else { evaluation.approval = -1; }
                        }

                        if (evaluation.approval === null) {
                            progress++;
                        }
                    })
                }
                else if (evalChanged3) {
                    evalChanged3 = false;
                    progress = 0;
                    // place 3rd preferences that fit in iias
                    evaluations.forEach((evaluation) => {
                        if (evaluation.preferenceOrder == 3 && evaluation.approval === null) {
                            if (iiaList[evaluation.iia.id].filledPositions < iiaList[evaluation.iia.id].mobilityNumber) {
                                iiaList[evaluation.iia.id].filledPositions++;

                                evaluation.approval = iiaList[evaluation.iia.id].filledPositions;
                            }
                            // if it doesn't fit, we find other entries with lower score for the same iia and remove the lowest one
                            else if (iiaList[evaluation.iia.id].filledPositions >= iiaList[evaluation.iia.id].mobilityNumber) {
                                let tmp = [];

                                evaluations.forEach((evaluation1) => {
                                    if (evaluation.iia.id == evaluation1.iia.id && evaluation.finalScore > evaluation1.finalScore) {
                                        tmp.push(evaluation1);
                                    }
                                })
                                if (tmp.length > 0) {
                                    tmp.sort((a, b) => a.finalScore - b.finalScore);

                                    evaluation.approval = iiaList[evaluation.iia.id].filledPositions;
                                    tmp[0].approval = -1;
                                    evaluations.forEach((evaluation1) => {
                                        if (evaluation1.applicationId == tmp[0].applicationId && evaluation1.id != tmp[0].id && evaluation1.approval === 0) {
                                            evaluation1.approval = null;
                                        }
                                    })

                                    // if we replaced an existing evaluation, if it had prefferenceOrder 1, we enabled some new entries with prefferenceOrder 2, 
                                    // so we repeat step 2 until nothing changes, before moving to step 3
                                    if (tmp[0].preferenceOrder == 1) {
                                        evalChanged2 = true;
                                    }
                                    // if we replaced an existing evaluation, if it had prefferenceOrder 2, we enabled some new entries with prefferenceOrder 3, 
                                    // so we repeat step 3 until nothing changes
                                    else if (tmp[0].preferenceOrder == 2) {
                                        evalChanged3 = true;
                                    }
                                    // if we replaced an existing evaluation, if it had prefferenceOrder 3, it gets disabled and we do no more actions
                                }
                            }
                            else { evaluation.approval = -1; }
                        }

                        if (evaluation.approval === null) {
                            progress++;
                        }
                    })
                }
            }

            console.log(iiaList);
            console.log(evaluations);

            // save placements
            const context = new ReactDataContext(process.env.REACT_APP_API_SERVER);
            context.setBearerAuthorization(props.userService.getToken());

            for (let i = 0; i < evaluations.length; i++) {
                setPlacementProggress(
                    {
                        header: "Saving updated placements. Please wait.",
                        body: i + " out of " + evaluations.length + " entries saved.",
                        inProgress: true
                    });
                const result = await context.model('ApplicationEvaluations').save(evaluations[i]);
                console.log(result);
            }

            setPlacementProggress(
                {
                    header: "Automatic placement completed.",
                    body: evaluations.length + " out of " + evaluations.length + " entries saved.",
                    inProgress: false
                });
        }

        return (
            <div className="py-5" ref={topRef}>
                <div className="container-xl">
                    <div className="row">
                        <div className="col-md-3 bg-light pt-2 filter-sidebar mx-auto h-100">
                            <div className="row">
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("mandatoryFilters", langCode)['label']}</p>
                                </div>
                                <div className="form-group">
                                    {!facultyDepartmentId && (
                                        <DropdownSelect
                                            optionsList={departments ? langCode === "gr" ? departments.map(i => ({ label: i?.nameGR, value: i?.nameGR })) : langCode === "en" ? departments.map(i => ({ label: i?.name, value: i?.name })) : [] : []}
                                            parentSetValueSelected={setFieldValue(setDepartmentSelected)}
                                            parentValueSelected={departmentSelected}
                                            selectLabel={EvaluationListTranslation("department", langCode)['label']}
                                            required={true}
                                            componentId="departmentSelected"
                                        />
                                    )}
                                    {facultyDepartmentId && (
                                        <label>{EvaluationListTranslation("department", langCode)['label']}: {departmentSelected}</label>
                                    )}
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={dateSelected}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setDateSelected(e.value);
                                            } else {
                                                setDateSelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="dateSelected"
                                        placeholderText={EvaluationListTranslation("call", langCode)['label']}
                                        required={true}
                                        clearable={true}
                                        listOptions={(applicationYears || []).map((year) => ({
                                            label: year,
                                            value: year
                                        }))}
                                    >
                                        {EvaluationListTranslation("call", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                                <div className="col-md-12 mb-2">
                                    <button className="btn btn-primary btn-block" disabled={(false)} onClick={() => { openPlacementModal() }}>Auto Placements</button>
                                </div>
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("sorting", langCode)['label']}</p>
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedSortingLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setSortSelected(e.value);
                                            } else {
                                                setSortSelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="sortSelected"
                                        placeholderText={EvaluationListTranslation("sorting", langCode)['label']}
                                        clearable={true}
                                        listOptions={sortingOptions}
                                    >
                                        {EvaluationListTranslation("sorting", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("optionalFilters", langCode)['label']}</p>
                                </div>
                                {facultyDepartmentId && (
                                    <div className="form-group">
                                        <SelectWithLabel
                                            value={selectedIiaDepartmentLabel}
                                            onSelectChange={(e) => {
                                                if (e !== null) {
                                                    setIiaDepartmentSelected(e.value);
                                                } else {
                                                    setIiaDepartmentSelected(null);
                                                }
                                                setPage(0);
                                            }}
                                            id="iiaDepartmentSelected"
                                            placeholderText={EvaluationListTranslation("iiaDepartment", langCode)['label']}
                                            clearable={true}
                                            listOptions={iiaDepartments.map((dept) => ({
                                                label: langCode === "gr" ? (dept?.nameGR || dept?.name) : dept?.name,
                                                value: dept?.id
                                            }))}
                                        >
                                            {EvaluationListTranslation("iiaDepartment", langCode)['label']}
                                        </SelectWithLabel>
                                    </div>
                                )}
                                <div className="form-group">
                                    <label for="universitySelected">{EvaluationListTranslation("universityName", langCode)['label']}</label>
                                    <input type='text'
                                        value={universitySelected ? universitySelected : ""}
                                        className="form-control"
                                        id="universitySelected"
                                        onChange={(e) => {
                                            if (e !== null) {
                                                setUniversitySelected(e.target.value)
                                            }
                                            else {
                                                setUniversitySelected(null)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label for="erasmusCodeSelected">{EvaluationListTranslation("erasmusCode", langCode)['label']}</label>
                                    <input type='text'
                                        value={erasmusCodeSelected ? erasmusCodeSelected : ""}
                                        className="form-control"
                                        id="erasmusCodeSelected"
                                        onChange={(e) => {
                                            if (e !== null) {
                                                setErasmusCodeSelected(e.target.value)
                                            }
                                            else {
                                                setErasmusCodeSelected(null)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedCountryLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setCountrySelected(e.value);
                                            } else {
                                                setCountrySelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="countrySelected"
                                        placeholderText={EvaluationListTranslation("country", langCode)['label']}
                                        clearable={true}
                                        listOptions={(countries || []).map((country) => ({
                                            label: country?.countryName,
                                            value: country?.id
                                        }))}
                                    >
                                        {EvaluationListTranslation("country", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedDepartmentalCoordinatorLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setDepartmentalCoordinatorSelected(e.value);
                                            } else {
                                                setDepartmentalCoordinatorSelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="departmentalCoordinatorSelected"
                                        placeholderText={EvaluationListTranslation("departmentalCoordinatorFilter", langCode)['label']}
                                        clearable={true}
                                        listOptions={(departmentalCoordinators || []).map((coordinator) => ({
                                            label: coordinator?.label,
                                            value: coordinator?.id
                                        }))}
                                    >
                                        {EvaluationListTranslation("departmentalCoordinatorFilter", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-9 pl-3 mx-auto">
                            {!hasMandatoryFilters ? <div className="d-flex justify-content-center">
                                <p>{EvaluationListTranslation("mandatoryFiltersMessage", langCode)['label']}</p>
                            </div> : (totalRows > 0) ? <div id="no-more-tables">
                                <table className="table-striped table-condensed cf table-responsive">
                                    <thead className="cf" style={{ backgroundColor: "#3A435E", color: "white" }}>
                                        <tr>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("fullNameList", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("erasmusCode", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("universityName", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("iia", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("order", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("evaluation", langCode)['label']}</th>
                                            <th scope="col" className="col-md-2 text-center">{EvaluationListTranslation("approval", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("actions", langCode)['label']}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRows.map((row) => {
                                            const iia = row?.mobility?.iiaId?.studentMobilitiesForStudies?.find(k => k?.fromInstitution?.heiId === process.env.REACT_APP_LOCAL_HEI_ID)

                                            return (
                                                <tr className="text-center" key={row.key}>
                                                    <td data-title="Full Name">{langCode === 'en' ? row?.application?.studentData?.[0]?.familyNameLatin + " " + row?.application?.studentData?.[0]?.nameLatin : row?.application?.studentData?.[0]?.familyNameGreek + " " + row?.application?.studentData?.[0]?.nameGreek}</td>
                                                    <td data-title="Erasmus Code">{row?.mobility?.institution?.erasmusCode}</td>
                                                    <td data-title="University">{row?.mobility?.institution?.name}</td>
                                                    <td data-title="IIA">
                                                        <>Mobilities: {iia?.mobilitiesPerYear}</><br />
                                                        <>Months: {iia?.totalMonthsPerYear}</><br />
                                                        <>Study Cycle: {iia?.studyCycles?.map((k, l) => {
                                                            if (l + 1 === iia?.studyCycles?.length) {
                                                                return <>{k.studyCycle}</>;
                                                            }
                                                            return <>{k.studyCycle}, </>;
                                                        })}</>
                                                    </td>
                                                    <td data-title="Preference order">{row?.preferenceOrder}</td>
                                                    <td data-title="Evaluation">{row?.finalScore}</td>
                                                    <td data-title="Approval">{row?.approval}</td>
                                                    <td data-title="Actions">
                                                        <div style={{ display: "flex" }}>
                                                            <div onClick={() => {
                                                                setSelectedRow(row)
                                                                setIsModalOpen(true)
                                                            }}><i className='fa fa-eye fa-primary m-2'></i></div>
                                                            <Restricted to="application_edit">
                                                                <div onClick={() => {
                                                                    setSelectedRow(row)
                                                                    setIsModalEditOpen(true)
                                                                }}><i className='fa fa-pencil fa-primary m-2'></i></div>
                                                            </Restricted>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div> : <div className="d-flex justify-content-center">
                                <p>{EvaluationListTranslation("noStudiesFound", langCode)['label']}</p>
                            </div>}
                            <div>
                                {isModalOpen && selectedRow && (
                                    <EvaluationModal
                                        show={isModalOpen}
                                        langCode={langCode}
                                        onClose={() => {
                                            setIsModalOpen(false)
                                            setSelectedRow(null)
                                        }}
                                        documentNumber={selectedRow?.application?.applicationNumber}
                                        studySemester={selectedRow?.application?.studiesData?.[selectedRow?.prefIndex]?.semesterDuringMobility}
                                        country={selectedRow?.mobility?.country?.countryName}
                                        agreementOfficer={selectedRow?.mobility?.iiaId?.departmentalCoordinatorContacts?.find(coord => coord?.roles?.[0]?.value === "Departmental Coordinator")}
                                        department={selectedRow?.mobility?.iiaId?.departmentalCoordinatorContacts?.find(coord => coord?.roles?.[0]?.value === "Departmental Coordinator")?.department?.name}
                                        language={selectedRow?.application?.languageData?.[selectedRow?.prefIndex]?.language?.languageName}
                                        languageDescription={selectedRow?.application?.languageData?.[selectedRow?.prefIndex]?.certificateLevel}
                                        languageLevel={selectedRow?.application?.evaluationData?.[selectedRow?.prefIndex]?.v1 * selectedRow?.application?.evaluationData?.[selectedRow?.prefIndex]?.w1}
                                        averageGrade="8.17"
                                        studyYearWeight="0.95"
                                        incentive="1"
                                        comments="Ο υποψήφιος κατέχει άριστη γνώση της αγγλικής γλώσσας που είναι σημαντικό για να παρακολουθήσει τα μαθήματα στο πανεπιστήμιο το οποίο προσφέρει προγράμματα σπουδών στην αγγλική."

                                    />
                                )}
                                {isModalEditOpen && selectedRow && (
                                    <EvaluationEditModal
                                        show={isModalEditOpen}
                                        langCode={langCode}
                                        onClose={() => {
                                            setIsModalEditOpen(false)
                                            setSelectedRow(null)
                                        }}
                                        selectedRow={selectedRow}
                                        userService={props.userService}
                                    />
                                )}

                                <Modal show={showPlacementModal}>
                                    <Modal.Header>
                                        <h4>
                                            {placementProggress.header}
                                        </h4>
                                    </Modal.Header>
                                    <Modal.Body>
                                        {placementProggress.body}
                                    </Modal.Body>
                                    <Modal.Footer>
                                        <button className="btn btn-primary btn-block" disabled={(placementProggress.inProgress || placementProggress.body == "Please wait.")} onClick={() => { startAutoPlacement() }}>Start</button>
                                        <button className="btn btn-danger btn-block" disabled={(placementProggress.inProgress)} onClick={() => { setShowPlacementModal(false) }}>Close</button>
                                    </Modal.Footer>
                                </Modal>
                            </div>

                            {
                                totalRows !== 0
                                    ?
                                    <div className="d-flex justify-content-center m-2">
                                        {/* feature idea do not show previous button if the user is in the first or last page etc */}
                                        <Pagination>
                                            <Pagination.First onClick={() => { if (totalRows !== 0) setPageDebounced(0); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            <Pagination.Prev onClick={() => { if (page !== 0) setPageDebounced(page - 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            {paginationItems.length > 3 ? <Pagination.Ellipsis /> : <></>}
                                            {paginationItems}
                                            {paginationItems.length > 3 ? <Pagination.Ellipsis /> : <></>}
                                            <Pagination.Next onClick={() => { if (page !== Math.ceil(totalRows / pageLimit) - 1) setPageDebounced(page + 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            <Pagination.Last onClick={() => { setPageDebounced(Math.ceil(totalRows / pageLimit) - 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                        </Pagination>
                                    </div>
                                    :
                                    <></>
                            }
                        </div>
                    </div>
                </div>
            </div >
        )
    }
}
